import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Eye, Trash2, Edit2, LayoutList, RefreshCw, Loader2, BookmarkPlusIcon, Code } from 'lucide-react';
import { PreviewModal } from '../components/PreviewModal';
import { FormPreview } from '../components/FormPreview';
import { signupFormsApi, SignupFormRecord, formConfigsApi, newslettersApi, extractLookupId, formTemplatesLibraryApi } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function SignupForms() {
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const team = currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const [forms, setForms] = useState<SignupFormRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewForm, setPreviewForm] = useState<SignupFormRecord | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [savingAsTemplate, setSavingAsTemplate] = useState<string | null>(null);
  const fetchForms = async () => {
    setLoading(true);
    try {
      const data = await signupFormsApi.list();
      setForms(data);
    } catch (err) {
      console.error('Failed to fetch signup forms:', err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchForms();
  }, []);
  const handleSaveAsTemplate = async (formId: string) => {
    setSavingAsTemplate(formId);
    try {
      const form = await signupFormsApi.get(formId);
      if (!form) {
        toast.error('Could not load form data');
        return;
      }
      await formTemplatesLibraryApi.create({
        Template_Name: (form.Form_Name || 'Untitled') + ' (Template)',
        HTML: form.HTML || '',
        CSS: form.CSS || '',
        JavaScript1: form.JavaScript1 || '',
        Builder_Schema: form.Builder_Schema || '',
        Builder_Mode: form.Builder_Mode || 'code',
        Team: team,
        Region: form.Region || currentSubRegion || ''
      });
      toast.success('Saved as form template', {
        description: `"${form.Form_Name}" saved to the template library.`,
        action: {
          label: 'View Templates',
          onClick: () => navigate('/form-templates')
        },
        duration: 5000
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to save as template', {
        description: msg
      });
    } finally {
      setSavingAsTemplate(null);
    }
  };
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      let linkedConfigCount = 0;
      let linkedNewsletterCount = 0;
      try {
        const linkedConfigs = await formConfigsApi.findByFormId(id);
        for (const config of linkedConfigs) {
          await formConfigsApi.delete(config.ID);
          linkedConfigCount++;
        }
      } catch (cleanupErr) {
        console.warn('Failed to clean up linked configurations:', cleanupErr);
      }
      try {
        const allNewsletters = await newslettersApi.list();
        const linkedNewsletters = allNewsletters.filter(nl => {
          const nlFormId = extractLookupId(nl.Sign_Up_Form);
          return nlFormId === id;
        });
        linkedNewsletterCount = linkedNewsletters.length;
      } catch (nlErr) {
        console.warn('Failed to check linked newsletters:', nlErr);
      }
      await signupFormsApi.delete(id);
      setForms(prev => prev.filter(f => f.ID !== id));
      setDeleteConfirm(null);
      if (linkedConfigCount > 0 || linkedNewsletterCount > 0) {
        const parts: string[] = [];
        if (linkedConfigCount > 0) parts.push(`${linkedConfigCount} configuration(s) removed`);
        if (linkedNewsletterCount > 0) parts.push(`${linkedNewsletterCount} newsletter(s) still reference this form`);
        toast.success('Signup form deleted', {
          description: parts.join('. '),
          duration: linkedNewsletterCount > 0 ? 8000 : 4000
        });
      } else {
        toast.success('Signup form deleted');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to delete form', {
        description: msg
      });
    } finally {
      setIsDeleting(false);
    }
  };
  const getStatusBadge = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s === 'active') return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-4408">
          Active
        </span>;
    if (s === 'draft') return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-4409">
          Draft
        </span>;
    if (s === 'archived') return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600" data-id="element-4410">
          Archived
        </span>;
    return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600" data-id="element-4411">
        {status || '—'}
      </span>;
  };
  // Filter by team and region
  const filteredForms = forms.filter(f => {
    if (f.Team !== currentWorkspace.name && f.Team !== team) return false;
    if (currentSubRegion) {
      return !f.Region || f.Region === currentSubRegion;
    }
    return true;
  });
  return <div className="space-y-6" data-id="element-4412">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-4413">
        <div className="min-w-0" data-id="element-4414">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4415">Signup Forms</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-4416">
            Manage your custom HTML signup forms stored in Zoho Creator.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0" data-id="element-4417">
          <button onClick={fetchForms} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" title="Refresh" data-id="element-4418">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-4419" />
          </button>
          <Link to="/forms/new" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap" data-id="element-4420">
            <Plus className="h-4 w-4" data-id="element-4421" />
            <span data-id="element-4422">Create Form</span>
          </Link>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-4423">
        {loading && forms.length === 0 ? <div className="p-12 text-center" data-id="element-4424">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-4425" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-4426">
              Loading signup forms...
            </p>
          </div> : filteredForms.length === 0 ? <div className="p-12 text-center" data-id="element-4427">
            <LayoutList className="mx-auto h-12 w-12 text-gray-400" data-id="element-4428" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-4429">
              No forms found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-4430">
              Create your first signup form to get started.
            </p>
            <Link to="/forms/new" className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-4431">
              <Plus className="h-4 w-4" data-id="element-4432" />
              Create First Form
            </Link>
          </div> : <div className="overflow-x-auto" data-id="element-4433">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-4434">
              <thead className="bg-gray-50" data-id="element-4435">
                <tr data-id="element-4436">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4437">
                    Form Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4438">
                    Slug
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4439">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4440">
                    Team
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4441">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-4442">
                {filteredForms.map(form => <tr key={form.ID} className="hover:bg-gray-50" data-id="element-4443">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-4444">
                      <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" data-id="element-4445">
                        {form.Form_Name || '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-4446">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600" data-id="element-4447">
                        {form.Url_Slug || '—'}
                      </code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-4448">
                      {getStatusBadge(form.Status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4449">
                      {form.Team || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-4450">
                      <div className="flex items-center justify-end gap-2" data-id="element-4451">
                        {form.HTML && <button onClick={() => setPreviewForm(form)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Preview" data-id="element-4452">
                            <Eye className="h-4 w-4" data-id="element-4453" />
                          </button>}
                        <Link to={`/forms/${form.ID}`} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Edit" data-id="element-4454">
                          <Edit2 className="h-4 w-4" data-id="element-4455" />
                        </Link>
                        <Link to={`/forms/${form.ID}/embed`} className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded" title="Embed Code" data-id="element-4456">
                          <Code className="h-4 w-4" data-id="element-4457" />
                        </Link>
                        <button onClick={() => handleSaveAsTemplate(form.ID)} disabled={savingAsTemplate === form.ID} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded disabled:opacity-50" title="Save as Template" data-id="element-4458">
                          {savingAsTemplate === form.ID ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-4459" /> : <BookmarkPlusIcon className="h-4 w-4" data-id="element-4460" />}
                        </button>
                        <button onClick={() => setDeleteConfirm(form.ID)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete" data-id="element-4461">
                          <Trash2 className="h-4 w-4" data-id="element-4462" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Preview Modal */}
      <PreviewModal isOpen={!!previewForm} onClose={() => setPreviewForm(null)} title={`Preview: ${previewForm?.Form_Name}`} data-id="element-4463">
        {previewForm && <FormPreview html={previewForm.HTML} css={previewForm.CSS} data-id="element-4464" />}
      </PreviewModal>

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-4465">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-4466">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-4467" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-4468">
              <div className="flex items-center gap-4" data-id="element-4469">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-4470">
                  <Trash2 className="h-6 w-6 text-red-600" data-id="element-4471" />
                </div>
                <div data-id="element-4472">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-4473">
                    Delete Signup Form
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-4474">
                    Are you sure? This will permanently remove the form from
                    Zoho Creator.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6" data-id="element-4475">
                <button onClick={() => setDeleteConfirm(null)} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4476">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-4477">
                  {isDeleting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-4478" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}